import { styled } from 'styled-components';
import { device, typography } from '@/styles/helpers';
import { ScTournamentBadge } from './components/TournamentBadge/styled';

export const ScTournamentCard = styled.div`
  container-type: inline-size;
  container-name: tournament-card;
  position: relative;
  border-radius: 16px;
  background-position: center -172px;
  background-size: 100% 172px;
  background-repeat: no-repeat;
  background-color: ${({ theme }) => theme.grayDarkColor};
  transition: background-position 0.2s ease-in-out;
  box-shadow:
    0 0 8px 0 rgba(0, 0, 0, 0.6),
    0 0 12px 0 rgba(0, 0, 0, 0.8);

  & .tournament-card-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    padding: 54px 12px 8px;
    position: relative;
    height: 100%;
    justify-content: space-between;

    @container tournament-card (width > 626px) {
      padding: 0 64px 0 32px;
      flex-direction: row;
      justify-content: center;
      column-gap: 8px;
    }
  }

  & .card-additional-description {
    display: flex;
    padding: 12px 16px 16px 16px;
    justify-content: center;
    align-items: center;
    gap: 24px;
    border-radius: 0 0 16px 16px;
    background: linear-gradient(120deg, #ca486a 34.84%, #b418e2 103.6%);

    @media (${device.mobile}) {
      gap: 8px;
      padding: 8px 12px 12px 12px;
      .description-icon {
        width: 44px;
        height: 48px;
      }
    }
    .description-text {
      ${typography.body1regular};
      @media (${device.mobile}) {
        ${typography.body2regular};
      }
    }
  }

  & .in-house-badge {
    position: absolute;
    right: 16px;
    top: -16px;
    color: ${({ theme }) => theme.blackColor};
    font-size: 14px;
    font-weight: 700;
    line-height: 100%;
    letter-spacing: 0.42px;
    text-transform: uppercase;
    padding: 1px 10px;
    border-radius: 4px 4px 0 0;
    background: ${({ theme }) => theme.yellowColor};
    box-shadow: 0 -4px 4px 0 rgba(0, 0, 0, 0.25) inset;
    @container tournament-card (width > 626px) {
      right: 32px;
    }
  }

  ${ScTournamentBadge} {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 1;

    @container tournament-card (width > 626px) {
      top: 18px;
      left: 16px;
    }
  }

  & .banner {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    width: 320px;
    height: 200px;
    max-width: 100%;
    transition: scale 0.2s ease-in-out;
    transform-origin: bottom center;

    & > img {
      max-width: 100%;
      object-fit: cover;
    }

    & > .bottom-gradient {
      position: relative;
      background-image: linear-gradient(
        0deg,
        #1c1c1e 0%,
        rgba(28, 28, 30, 0.9) 29.31%,
        rgba(28, 28, 30, 0) 100%
      );
      width: 100%;
      height: 64px;
      z-index: 1;
    }

    @container tournament-card (width > 626px) {
      width: 100%;
      max-width: 560px;
      height: 313px;
      transform-origin: center;
    }
  }

  & .card-footer {
    text-align: center;
    width: 100%;

    @container tournament-card (width > 626px) {
      padding-block: 80px 32px;
      max-width: 385px;
    }

    & .name,
    & .prize {
      font-weight: 700;
      line-height: 100%;
      text-transform: uppercase;
    }

    & .name {
      position: relative;
      color: ${({ theme }) => theme.secondaryColor};
      font-size: 18px;
      margin-bottom: 4px;
      letter-spacing: 0.54px;
      height: 36px;
      z-index: 1;
      margin-top: -18px;
      display: flex;
      align-items: end;
      justify-content: center;

      & > p {
        @container tournament-card (width < 626px) {
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
        }
      }
    }

    & .prize {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 34px;
      letter-spacing: 1.02px;
      margin-bottom: 16px;
      @media (${device.mobile}) {
        ${typography.headline1mobile};
      }

      @container tournament-card (width > 626px) {
        font-size: 44px;
        line-height: 44px;
        letter-spacing: 1.44px;
        margin-bottom: 40px;
      }
    }

    & .cta-box {
      @container tournament-card (width > 626px) {
        & > button {
          max-width: 305px;
          margin-inline: auto;
        }
      }
    }
  }

  &.finished {
    & .banner,
    & .card-footer .name {
      filter: grayscale(1);
    }
  }

  &.purple-gradient {
    background-image: linear-gradient(
      180deg,
      #7f43ea 0%,
      rgba(128, 68, 234, 0.32) 52.7%,
      rgba(127, 67, 234, 0) 100%
    );
  }

  &.gold-gradient {
    background-image: linear-gradient(
      180deg,
      #d2a215 0%,
      rgba(241, 198, 66, 0.32) 52.7%,
      rgba(210, 162, 21, 0) 100%
    );

    & .card-footer .name {
      color: ${({ theme }) => theme.yellowColor};
    }
  }

  &.red-gradient {
    background-image: linear-gradient(
      180deg,
      #e53808 0%,
      rgba(229, 56, 8, 0.3) 52.7%,
      rgba(229, 56, 8, 0) 100%
    );
  }

  &.blue-gradient {
    background-image: linear-gradient(
      180deg,
      #3295ed 0%,
      rgba(72, 82, 235, 0.32) 52.7%,
      rgba(84, 51, 235, 0) 100%
    );
  }

  &.green-gradient {
    background-image: linear-gradient(
      180deg,
      #16a753 0%,
      rgba(7, 123, 64, 0.32) 52.7%,
      rgba(22, 167, 83, 0) 100%
    );
  }
  &.orange-gradient {
    background-image: linear-gradient(
      180deg,
      #ff9e01 0%,
      rgba(255, 158, 1, 0.32) 52.7%,
      rgba(127, 67, 234, 0) 100%
    );
  }
  &.pink-gradient {
    background-image: linear-gradient(
      180deg,
      #ca486a 0%,
      rgba(118, 93, 157, 0.32) 52.7%,
      rgba(127, 67, 234, 0) 100%
    );
  }

  &:not(.without-hover):hover {
    &:not(.upcoming):not(.finished) {
      background-position: center 0px;
    }

    & .banner {
      scale: 1.05;
    }
  }
`;
